import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { useNavigate } from 'react-router-dom';
import TemplatePickerModal from './TemplatePickerModal';
import OfficeHoursHint from './OfficeHoursHint';

/**
 * TabAnnouncements - Unified announcements with template support
 * Mobile-first with sticky footer and safe-area handling
 */
const TabAnnouncements = ({ prefilledData = null }) => {
  const navigate = useNavigate();
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If prefilled data provided (from post-pack nudge), show template picker
    if (prefilledData) {
      setSelectedTemplate(prefilledData.template);
    }
  }, [prefilledData]);

  const handleCreateAnnouncement = () => {
    // Navigate to existing announcements page with template data
    if (selectedTemplate) {
      navigate('/announcements/create', { 
        state: { 
          template: selectedTemplate,
          prefilled: prefilledData 
        } 
      });
    } else {
      navigate('/announcements/create');
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setShowTemplatePicker(false);
  };

  return (
    <div 
      className="flex flex-col h-full"
      style={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Scrollable Content Area */}
      <div 
        className="flex-1 overflow-y-auto px-6 pt-6"
        style={{
          flex: '1 1 auto',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 'calc(140px + env(safe-area-inset-bottom, 20px))'
        }}
      >
        {/* Header - Desktop Only */}
        <div className="hidden md:flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Announcements</h2>
            <p className="text-sm text-gray-600">
              System-wide announcements with template support and scheduling
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowTemplatePicker(true)}
              variant="outline"
              className="bg-blue-50 text-blue-600 hover:bg-blue-100"
            >
              📝 Pick Template
            </Button>
            <Button
              onClick={handleCreateAnnouncement}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              + Create Announcement
            </Button>
          </div>
        </div>

        {/* Mobile Header - Without Buttons */}
        <div className="md:hidden mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Announcements</h2>
          <p className="text-sm text-gray-600 mt-1">
            System-wide announcements with template support
          </p>
        </div>

      {/* Office Hours Hint */}
      <OfficeHoursHint 
        suggestedTime={new Date(Date.now() + 3600000).toISOString()}
      />

      {/* Selected Template Display */}
      {selectedTemplate && (
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-green-900">
                ✓ Template Ready: {selectedTemplate.title}
              </CardTitle>
              <Button
                onClick={() => setSelectedTemplate(null)}
                variant="ghost"
                size="sm"
                className="text-green-700 hover:text-green-900"
              >
                Clear
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-green-800 mb-3">
              Click "Create Announcement" to use this template
            </p>
            <div className="bg-white p-3 rounded border border-green-200">
              <p className="text-xs font-semibold text-gray-700 mb-2">Preview:</p>
              <p className="text-sm whitespace-pre-wrap">{selectedTemplate.content}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prefilled Data Banner */}
      {prefilledData && (
        <Card className="mb-6 border-purple-200 bg-purple-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <span className="text-2xl">📦</span>
              <div className="flex-1">
                <h3 className="font-semibold text-purple-900 mb-1">
                  Announcement Pre-filled from Knowledge Pack
                </h3>
                <p className="text-sm text-purple-800">
                  Pack: <strong>{prefilledData.pack?.pack_name}</strong>
                </p>
                <p className="text-sm text-purple-800">
                  Event: <strong>{prefilledData.event}</strong>
                </p>
                <Button
                  onClick={handleCreateAnnouncement}
                  size="sm"
                  className="mt-3 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Continue with Pre-filled Data
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

        {/* Info Card - Integration Notice */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                This tab integrates with your existing Announcements system.
              </p>
              {/* Desktop Buttons */}
              <div className="hidden md:flex justify-center gap-2">
                <Button
                  onClick={() => navigate('/announcements')}
                  variant="outline"
                >
                  View All Announcements
                </Button>
                <Button
                  onClick={handleCreateAnnouncement}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Create New Announcement
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Announcements Preview */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Recent Announcements</h3>
          <div className="text-center py-8 text-gray-500">
            <p>View and manage announcements from the main Announcements page</p>
            <Button
              onClick={() => navigate('/announcements')}
              variant="link"
              className="text-blue-600 mt-2 hidden md:inline-flex"
            >
              Go to Announcements →
            </Button>
          </div>
        </div>
      </div>

      {/* Sticky Action Footer - Mobile Only */}
      <div 
        className="md:hidden sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg"
        style={{
          position: 'sticky',
          bottom: 'max(env(safe-area-inset-bottom, 0px), 0px)',
          zIndex: 40,
          paddingBottom: 'env(safe-area-inset-bottom, 12px)'
        }}
      >
        <div className="px-4 py-3 flex flex-col gap-2">
          <Button
            onClick={() => setShowTemplatePicker(true)}
            variant="outline"
            className="w-full bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200 font-semibold"
            style={{ minHeight: '48px' }}
          >
            📝 Pick Template
          </Button>
          <Button
            onClick={handleCreateAnnouncement}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            style={{ minHeight: '48px' }}
          >
            + Create New Announcement
          </Button>
          <Button
            onClick={() => navigate('/announcements')}
            variant="outline"
            className="w-full text-gray-700 hover:bg-gray-50 font-medium text-sm"
            style={{ minHeight: '44px' }}
          >
            View All Announcements →
          </Button>
        </div>
      </div>

      {/* Template Picker Modal */}
      <TemplatePickerModal
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onSelectTemplate={handleSelectTemplate}
      />
    </div>
  );
};

export default TabAnnouncements;
